import { useState, useMemo } from 'react';
import {
  Info as InfoIcon,
  CaretDown as CaretDownIcon,
  CaretUp as CaretUpIcon,
  CaretRight as CaretRightIcon,
} from '@phosphor-icons/react';
import { cn } from '@/lib/utils';
import Page from '@/components/page/Page';

import {
  ALLOCATIONS,
  SNAPSHOTS,
  CURRENT_USER,
  REGION_LABEL,
  findCustomer,
  findProduct,
  type AllocationRow,
  type AllocStats,
} from './data';

import {
  DiffCell,
  ProvChips,
  RemainderCell,
  CustomerCell,
  ProductCell,
  StatusCell,
  PivotSwitcher,
  SnapshotComparator,
  FilterBar,
} from './components/AllocationsBits';
import { ReviewCard } from './components/ReviewCard';
import { ExpandedRow } from './components/ExpandedRow';
import { HorizonPivot } from './components/HorizonPivot';

const dense = true;
const showProvenance = true;

export function AllocationsPage() {
  const [pivot, setPivot] = useState('list');
  const [filters, setFilters] = useState({
    scope: 'mine',
    region: 'all',
    product: 'all',
    warehouse: 'all',
    onlyChanged: false,
    onlyOverConsumed: false,
    q: '',
  });
  const [compare, setCompare] = useState({ from: 'S-MAY12', to: 'S-MAY13' });
  const [expanded, setExpanded] = useState<string | null>(null);
  const [flagged, setFlagged] = useState<Set<string>>(() => new Set());
  const [sort, setSort] = useState({
    key: 'updated',
    dir: 'desc' as 'asc' | 'desc',
  });

  const toggleFlag = (rid: string) => {
    const n = new Set(flagged);
    n.has(rid) ? n.delete(rid) : n.add(rid);
    setFlagged(n);
  };

  // ── Filtering ──
  const filtered = useMemo(() => {
    let rows = ALLOCATIONS;
    if (filters.scope === 'mine') {
      rows = rows.filter(
        (r) => findCustomer(r.customer).salesperson === CURRENT_USER.id,
      );
    }
    if (filters.region !== 'all') {
      rows = rows.filter(
        (r) => findCustomer(r.customer).region === filters.region,
      );
    }
    if (filters.product !== 'all') {
      rows = rows.filter((r) => r.product === filters.product);
    }
    if (filters.warehouse !== 'all') {
      rows = rows.filter((r) => r.prov.some((p) => p.wh === filters.warehouse));
    }
    if (filters.onlyChanged) {
      rows = rows.filter((r) => r.qty[compare.to] !== r.qty[compare.from]);
    }
    if (filters.onlyOverConsumed) {
      rows = rows.filter((r) => r.remainder < 0);
    }
    if (filters.q.trim()) {
      const q = filters.q.toLowerCase();
      rows = rows.filter((r) => {
        const c = findCustomer(r.customer);
        const p = findProduct(r.product);
        return (
          c.name.toLowerCase().includes(q) ||
          c.id.toLowerCase().includes(q) ||
          p.name.toLowerCase().includes(q) ||
          p.id.toLowerCase().includes(q) ||
          p.sku.toLowerCase().includes(q)
        );
      });
    }
    return rows;
  }, [filters, compare]);

  // ── Sorting ──
  const sorted = useMemo(() => {
    const dir = sort.dir === 'asc' ? 1 : -1;
    const arr = [...filtered];
    arr.sort((a, b) => {
      let av: number | string = 0;
      let bv: number | string = 0;
      if (sort.key === 'customer') {
        av = findCustomer(a.customer).name;
        bv = findCustomer(b.customer).name;
      } else if (sort.key === 'product') {
        av = a.product;
        bv = b.product;
      } else if (sort.key === 'allocated') {
        av = a.qty[compare.to];
        bv = b.qty[compare.to];
      } else if (sort.key === 'diff') {
        av = a.qty[compare.to] - a.qty[compare.from];
        bv = b.qty[compare.to] - b.qty[compare.from];
      } else if (sort.key === 'consumed') {
        av = a.consumed;
        bv = b.consumed;
      } else if (sort.key === 'remainder') {
        av = a.remainder;
        bv = b.remainder;
      } else if (sort.key === 'updated') {
        av = a.qty[compare.to] !== a.qty[compare.from] ? 1 : 0;
        bv = b.qty[compare.to] !== b.qty[compare.from] ? 1 : 0;
      }
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
    return arr;
  }, [filtered, sort, compare]);

  // ── Stats ──
  const stats: AllocStats = useMemo(() => {
    const customers = new Set<string>();
    let totalAlloc = 0;
    let changedRows = 0;
    let upRows = 0;
    let downRows = 0;
    let overConsumed = 0;
    const whSet = new Set<string>();
    for (const r of filtered) {
      customers.add(r.customer);
      totalAlloc += r.qty[compare.to];
      const d = r.qty[compare.to] - r.qty[compare.from];
      if (d !== 0) {
        changedRows++;
        if (d > 0) upRows++;
        else downRows++;
      }
      if (r.remainder < 0) overConsumed++;
      r.prov.forEach((p) => whSet.add(p.wh));
    }
    return {
      totalAlloc,
      totalRows: filtered.length,
      totalCustomers: customers.size,
      changedRows,
      upRows,
      downRows,
      overConsumed,
      whCount: whSet.size,
      whLabels: [...whSet].sort().join(' · ') || '—',
    };
  }, [filtered, compare]);

  const fromSnap = SNAPSHOTS.find((s) => s.id === compare.from)!;
  const toSnap = SNAPSHOTS.find((s) => s.id === compare.to)!;

  return (
    <Page
      title="Product allocations"
      subtitle={`Week of May 11, 2026 (W19) · Latest snapshot ${toSnap.shortLabel} · Last refreshed 6m ago`}
      actions={
        <div className="flex items-end gap-2.5">
          <PivotSwitcher value={pivot} onChange={setPivot} />
          <SnapshotComparator
            from={compare.from}
            to={compare.to}
            onChange={setCompare}
          />
        </div>
      }
    >
      <div className="flex flex-col gap-2.5 pt-1">
        {/* Review card */}
        <ReviewCard
          stats={stats}
          filtered={filtered}
          compare={compare}
          dense={dense}
          onJumpTo={(rid) => setExpanded(rid)}
        />

        {/* Table / pivots */}
        <div>
          <FilterBar
            filters={filters}
            setFilters={setFilters}
            total={
              filters.scope === 'mine'
                ? ALLOCATIONS.filter(
                    (r) =>
                      findCustomer(r.customer).salesperson === CURRENT_USER.id,
                  ).length
                : ALLOCATIONS.length
            }
            shown={filtered.length}
          />
          {pivot === 'list' && (
            <ListPivot
              rows={sorted}
              compare={compare}
              sort={sort}
              setSort={setSort}
              expanded={expanded}
              setExpanded={setExpanded}
              flagged={flagged}
              onFlag={toggleFlag}
            />
          )}
          {pivot === 'grouped' && (
            <GroupedPivot
              rows={sorted}
              compare={compare}
              expanded={expanded}
              setExpanded={setExpanded}
              flagged={flagged}
              onFlag={toggleFlag}
            />
          )}
          {pivot === 'horizon' && (
            <HorizonPivot
              rows={filtered}
              dense={dense}
              flagged={flagged}
              onFlag={toggleFlag}
            />
          )}
        </div>

        <div className="flex items-center gap-1.5 px-0.5 text-[11px] text-muted-foreground">
          <InfoIcon size={12} />
          Click a row to inspect AI reasoning, provenance, and snapshot
          timeline.
        </div>
      </div>
    </Page>
  );
}

// ── List pivot ──

const thStyle =
  'p-[7px_10px] text-[11px] font-medium text-muted-foreground uppercase tracking-wider';
const tdStyle = 'p-[7px_10px] align-middle tabular-nums';

const GRID_COLS =
  '28px minmax(140px,1fr) minmax(140px,1fr) 100px 80px 260px 90px 90px 110px';
const GRID_COLS_NO_PROV =
  '28px minmax(180px,1fr) minmax(180px,1fr) 100px 80px 90px 90px 110px';

function SortableCol({
  k,
  sortState,
  setSort,
  align,
  children,
}: {
  k: string;
  sortState: { key: string; dir: string };
  setSort: (s: { key: string; dir: 'asc' | 'desc' }) => void;
  align?: string;
  children: React.ReactNode;
}) {
  const isActive = sortState.key === k;
  return (
    <div
      className={cn(thStyle, 'cursor-pointer select-none')}
      style={{ textAlign: (align as any) || 'left' }}
      onClick={() =>
        setSort({
          key: k,
          dir: isActive && sortState.dir === 'desc' ? 'asc' : 'desc',
        })
      }
    >
      <span
        className={cn(
          'inline-flex items-center gap-0.5',
          isActive && 'text-stone-900',
        )}
      >
        {children}
        {isActive &&
          (sortState.dir === 'desc' ? (
            <CaretDownIcon size={10} />
          ) : (
            <CaretUpIcon size={10} />
          ))}
      </span>
    </div>
  );
}

function ListPivot({
  rows,
  compare,
  sort: sortState,
  setSort,
  expanded,
  setExpanded,
  flagged,
  onFlag,
}: {
  rows: AllocationRow[];
  compare: { from: string; to: string };
  sort: { key: string; dir: string };
  setSort: (s: { key: string; dir: 'asc' | 'desc' }) => void;
  expanded: string | null;
  setExpanded: (v: string | null) => void;
  flagged: Set<string>;
  onFlag: (rid: string) => void;
}) {
  const fromSnap = SNAPSHOTS.find((s) => s.id === compare.from)!;
  const toSnap = SNAPSHOTS.find((s) => s.id === compare.to)!;

  return (
    <div className="overflow-hidden rounded-b-xl border border-t-0 border-border bg-white">
      <div
        className="overflow-auto"
        style={{ maxHeight: 'calc(100vh - 360px)' }}
      >
        <div
          className="sticky top-0 z-[2] grid items-center border-b border-border bg-stone-50"
          style={{
            gridTemplateColumns: showProvenance ? GRID_COLS : GRID_COLS_NO_PROV,
            borderLeft: '3px solid transparent',
          }}
        >
          <div className={thStyle} />
          <SortableCol k="customer" sortState={sortState} setSort={setSort}>
            Customer
          </SortableCol>
          <SortableCol k="product" sortState={sortState} setSort={setSort}>
            Product
          </SortableCol>
          <SortableCol
            k="allocated"
            sortState={sortState}
            setSort={setSort}
            align="right"
          >
            <span title={toSnap.label} className="whitespace-nowrap">
              Alloc · {toSnap.shortLabel.replace(' W', '')}
              {toSnap.kind === 'weekly' && (
                <span className="ml-0.5 text-[9px] text-indigo-700">◆</span>
              )}
            </span>
          </SortableCol>
          <SortableCol
            k="diff"
            sortState={sortState}
            setSort={setSort}
            align="right"
          >
            <span title={`Δ vs ${fromSnap.label}`}>
              Δ vs {fromSnap.shortLabel.replace(' W', '')}
              {fromSnap.kind === 'weekly' && (
                <span className="ml-0.5 text-[9px] text-indigo-700">◆</span>
              )}
            </span>
          </SortableCol>
          {showProvenance && <div className={thStyle}>Provenance</div>}
          <SortableCol
            k="consumed"
            sortState={sortState}
            setSort={setSort}
            align="right"
          >
            Consumed
          </SortableCol>
          <SortableCol
            k="remainder"
            sortState={sortState}
            setSort={setSort}
            align="right"
          >
            Remainder
          </SortableCol>
          <SortableCol k="updated" sortState={sortState} setSort={setSort}>
            Status
          </SortableCol>
        </div>
        <table className="w-full border-collapse text-xs">
          <tbody className="[&>tr:last-child>td]:border-b-0">
            {rows.map((row) => {
              const cust = findCustomer(row.customer);
              const prod = findProduct(row.product);
              const cur = row.qty[compare.to];
              const prev = row.qty[compare.from];
              const isExpanded = expanded === row.rid;
              const isOver = row.remainder < 0;
              const isChanged = cur !== prev;
              const isFlagged = flagged.has(row.rid);
              return (
                <tr key={row.rid}>
                  <td colSpan={9} className="p-0 border-b border-border">
                    <div
                      className={cn(
                        'grid cursor-pointer items-center',
                        isOver
                          ? 'bg-red-50/50'
                          : isExpanded
                            ? 'bg-stone-50'
                            : 'bg-white hover:bg-stone-50/50',
                        isExpanded && 'border-b border-border',
                      )}
                      style={{
                        gridTemplateColumns: showProvenance
                          ? GRID_COLS
                          : GRID_COLS_NO_PROV,
                        borderLeft: isFlagged
                          ? '3px solid #b45309'
                          : '3px solid transparent',
                      }}
                      onClick={() => setExpanded(isExpanded ? null : row.rid)}
                    >
                      <div
                        className={cn(
                          tdStyle,
                          'text-center text-muted-foreground',
                        )}
                      >
                        {isExpanded ? (
                          <CaretDownIcon size={11} />
                        ) : (
                          <CaretRightIcon size={11} />
                        )}
                      </div>
                      <div className={tdStyle}>
                        <CustomerCell cust={cust} dense={dense} />
                      </div>
                      <div className={tdStyle}>
                        <ProductCell prod={prod} dense={dense} />
                      </div>
                      <div className={cn(tdStyle, 'text-right')}>
                        <span className="text-[13px] font-semibold tabular-nums text-stone-900">
                          {cur}
                        </span>
                        <span className="text-[10px] text-muted-foreground">
                          {' '}
                          {prod.uom}
                        </span>
                      </div>
                      <div className={cn(tdStyle, 'text-right')}>
                        <DiffCell cur={cur} prev={prev} />
                      </div>
                      {showProvenance && (
                        <div className={tdStyle}>
                          <ProvChips prov={row.prov} />
                        </div>
                      )}
                      <div className={cn(tdStyle, 'text-right')}>
                        <span className="text-xs tabular-nums text-stone-900">
                          {row.consumed}
                        </span>
                        <span className="text-[10px] text-muted-foreground">
                          {' '}
                          {prod.uom}
                        </span>
                      </div>
                      <div className={cn(tdStyle, 'text-right')}>
                        <RemainderCell
                          remainder={row.remainder}
                          uom={prod.uom}
                        />
                      </div>
                      <div className={tdStyle}>
                        <StatusCell
                          row={row}
                          isChanged={isChanged}
                          isFlagged={isFlagged}
                        />
                      </div>
                    </div>
                    {isExpanded && (
                      <table className="w-full border-collapse">
                        <tbody>
                          <tr>
                            <ExpandedRow
                              row={row}
                              fromSnap={compare.from}
                              toSnap={compare.to}
                              onFlag={onFlag}
                              flagged={isFlagged}
                            />
                          </tr>
                        </tbody>
                      </table>
                    )}
                  </td>
                </tr>
              );
            })}
            {rows.length === 0 && (
              <tr>
                <td
                  colSpan={9}
                  className="p-10 text-center text-[13px] text-muted-foreground"
                >
                  No allocations match your filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Grouped pivot ──

function GroupedPivot({
  rows,
  compare,
  expanded,
  setExpanded,
  flagged,
  onFlag,
}: {
  rows: AllocationRow[];
  compare: { from: string; to: string };
  expanded: string | null;
  setExpanded: (v: string | null) => void;
  flagged: Set<string>;
  onFlag: (rid: string) => void;
}) {
  const groups = useMemo(() => {
    const m = new Map<string, AllocationRow[]>();
    for (const r of rows) {
      if (!m.has(r.customer)) m.set(r.customer, []);
      m.get(r.customer)!.push(r);
    }
    return [...m.entries()]
      .map(([cid, items]) => ({ cust: findCustomer(cid), items }))
      .sort((a, b) => a.cust.name.localeCompare(b.cust.name));
  }, [rows]);

  return (
    <div className="overflow-hidden rounded-b-xl border border-t-0 border-border bg-white">
      <div
        className="overflow-auto"
        style={{ maxHeight: 'calc(100vh - 360px)' }}
      >
        {groups.map(({ cust, items }) => {
          const totalAlloc = items.reduce((s, r) => s + r.qty[compare.to], 0);
          const overCount = items.filter((r) => r.remainder < 0).length;
          const changedCount = items.filter(
            (r) => r.qty[compare.to] !== r.qty[compare.from],
          ).length;
          return (
            <div key={cust.id} className="border-b border-border">
              <div className="flex items-center gap-2.5 border-b border-border bg-stone-50 px-3 py-2">
                <CustomerCell cust={cust} dense={false} />
                <div className="flex-1" />
                <span className="text-[11px] text-muted-foreground">
                  {items.length} {items.length === 1 ? 'product' : 'products'} ·{' '}
                  {totalAlloc} units total
                </span>
                {changedCount > 0 && (
                  <span className="rounded bg-amber-100 px-2 py-px text-[10px] font-semibold text-amber-800">
                    {changedCount} changed
                  </span>
                )}
                {overCount > 0 && (
                  <span className="rounded bg-red-100 px-2 py-px text-[10px] font-semibold text-red-700">
                    {overCount} over-consumed
                  </span>
                )}
              </div>
              <table className="w-full border-collapse text-xs">
                <tbody>
                  {items.map((row) => {
                    const prod = findProduct(row.product);
                    const cur = row.qty[compare.to];
                    const prev = row.qty[compare.from];
                    const isExpanded2 = expanded === row.rid;
                    const isOver = row.remainder < 0;
                    const isChanged = cur !== prev;
                    const isFlagged = flagged.has(row.rid);
                    return (
                      <tr key={row.rid}>
                        <td colSpan={7} className="p-0 border-b border-border">
                          <div
                            className={cn(
                              'grid cursor-pointer items-center',
                              isOver
                                ? 'bg-red-50/50'
                                : isExpanded2
                                  ? 'bg-stone-50'
                                  : 'bg-white hover:bg-stone-50/50',
                              isExpanded2 && 'border-b border-border',
                            )}
                            style={{
                              gridTemplateColumns:
                                '34% 100px 100px 20% 110px 100px 24px',
                              borderLeft: isFlagged
                                ? '3px solid #b45309'
                                : '3px solid transparent',
                            }}
                            onClick={() =>
                              setExpanded(isExpanded2 ? null : row.rid)
                            }
                          >
                            <div className={cn(tdStyle, 'pl-9')}>
                              <ProductCell prod={prod} dense={dense} />
                            </div>
                            <div className={cn(tdStyle, 'text-right')}>
                              <span className="font-semibold tabular-nums text-stone-900">
                                {cur}
                              </span>
                              <span className="text-muted-foreground">
                                {' '}
                                {prod.uom}
                              </span>
                            </div>
                            <div className={cn(tdStyle, 'text-right')}>
                              <DiffCell cur={cur} prev={prev} />
                            </div>
                            <div className={tdStyle}>
                              <ProvChips prov={row.prov} />
                            </div>
                            <div className={cn(tdStyle, 'text-right')}>
                              <RemainderCell
                                remainder={row.remainder}
                                uom={prod.uom}
                              />
                            </div>
                            <div className={tdStyle}>
                              <StatusCell
                                row={row}
                                isChanged={isChanged}
                                isFlagged={isFlagged}
                              />
                            </div>
                            <div
                              className={cn(
                                tdStyle,
                                'text-center text-muted-foreground',
                              )}
                            >
                              {isExpanded2 ? (
                                <CaretDownIcon size={11} />
                              ) : (
                                <CaretRightIcon size={11} />
                              )}
                            </div>
                          </div>
                          {isExpanded2 && (
                            <table className="w-full border-collapse">
                              <tbody>
                                <tr>
                                  <ExpandedRow
                                    row={row}
                                    fromSnap={compare.from}
                                    toSnap={compare.to}
                                    onFlag={onFlag}
                                    flagged={isFlagged}
                                  />
                                </tr>
                              </tbody>
                            </table>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          );
        })}
        {groups.length === 0 && (
          <div className="p-10 text-center text-[13px] text-muted-foreground">
            No allocations match your filters.
          </div>
        )}
      </div>
    </div>
  );
}

export default AllocationsPage;
